// Enhanced DataTable V2 with updated pagination design - v2
// Updated: Pagination matches Notifications page with first/last buttons
import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import {
  ChevronUp, ChevronDown, ChevronsUpDown, Eye, EyeOff,
  Settings2, ChevronLeft, ChevronRight, Columns, GripVertical,
  ArrowUpDown, ArrowUp, ArrowDown, RefreshCw, Maximize2, MoreVertical
} from 'lucide-react';
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { TouchBackend } from 'react-dnd-touch-backend';
import { TableLoader, ListLoader } from './CommonLoader';
import tablePreferencesService from '../services/tablePreferencesService';

// Determine if we're on a touch device
const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
const dndBackend = isTouchDevice ? TouchBackend : HTML5Backend;

// Column Resizer Component
const ColumnResizer = ({ onResize, columnKey }) => {
  const [isResizing, setIsResizing] = useState(false);
  const [startX, setStartX] = useState(0);
  const [startWidth, setStartWidth] = useState(0);
  const resizerRef = useRef(null);

  const handleMouseDown = (e) => {
    const column = e.target.parentElement;
    setIsResizing(true);
    setStartX(e.clientX);
    setStartWidth(column.offsetWidth);
    e.preventDefault();
  };

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isResizing) return;
      const diff = e.clientX - startX;
      const newWidth = Math.max(50, startWidth + diff); // Min width 50px
      if (resizerRef.current && resizerRef.current.parentElement) {
        resizerRef.current.parentElement.style.width = `${newWidth}px`;
      }
      if (onResize) {
        onResize(columnKey, newWidth);
      }
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing, startX, startWidth, columnKey, onResize]);

  return (
    <div
      ref={resizerRef}
      className={`absolute right-0 top-0 bottom-0 w-1 cursor-col-resize hover:bg-primary-400 ${
        isResizing ? 'bg-primary-500' : 'bg-transparent'
      }`}
      onMouseDown={handleMouseDown}
      style={{ touchAction: 'none' }}
    />
  );
};

// Enhanced Draggable Column Header with Resize
const DraggableColumnHeader = ({ 
  column, 
  index, 
  moveColumn, 
  hidden, 
  onToggleHide, 
  sortConfig,
  onSort,
  width,
  onResize,
  showColumnSettings
}) => {
  const [{ isDragging }, drag, preview] = useDrag({
    type: 'column',
    item: { index, id: column.key },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
    canDrag: !showColumnSettings, // Disable drag when settings panel is open
  });

  const [{ isOver }, drop] = useDrop({
    accept: 'column',
    hover: (item) => {
      if (item.index !== index) {
        moveColumn(item.index, index);
        item.index = index;
      }
    },
    collect: (monitor) => ({
      isOver: monitor.isOver(),
    }),
  });

  const handleSort = () => {
    if (column.sortable !== false && onSort) {
      onSort(column.key);
    }
  };

  const getSortIcon = () => {
    if (column.sortable === false) return null;
    
    if (sortConfig?.key === column.key) {
      return sortConfig.direction === 'asc' ? (
        <ArrowUp className="w-3 h-3" />
      ) : (
        <ArrowDown className="w-3 h-3" />
      );
    }
    return <ChevronsUpDown className="w-3 h-3 opacity-50" />;
  };

  return (
    <th
      ref={(node) => {
        drag(drop(node));
        preview(node);
      }}
      className={`relative px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider ${
        isDragging ? 'opacity-50' : ''
      } ${isOver ? 'bg-gray-100' : ''} ${hidden ? 'opacity-30' : ''} ${
        column.sortable !== false ? 'hover:bg-gray-50 cursor-pointer select-none' : ''
      }`}
      onClick={handleSort}
      style={{ width: width || 'auto', minWidth: '100px' }}
    >
      <div className="flex items-center justify-between gap-2 pr-2">
        <div className="flex items-center gap-2">
          {!showColumnSettings && (
            <GripVertical className="w-3 h-3 opacity-50 cursor-move" />
          )}
          <span className="truncate">{column.header || column.label}</span>
          {getSortIcon()}
        </div>
        <button
          onClick={(e) => {
            e.stopPropagation();
            onToggleHide(column.key);
          }}
          className="p-0.5 hover:bg-gray-200 rounded transition-colors opacity-0 group-hover:opacity-100"
        >
          {hidden ? <EyeOff className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
        </button>
      </div>
      <ColumnResizer onResize={onResize} columnKey={column.key} />
    </th>
  );
};

/**
 * EnhancedDataTableV2 - Advanced table with all features
 * - Sorting
 * - Column visibility toggle
 * - Drag-and-drop column reordering
 * - Column resizing
 * - Responsive design
 */
const EnhancedDataTableV2 = ({
  columns: initialColumns = [],
  data = [],
  loading = false,
  emptyMessage = "No data found",
  actions,
  onRowClick,
  pagination,
  onPageChange,
  itemName = "items",
  onSort,
  defaultSortKey,
  defaultSortDirection = 'asc',
  className = "",
  showColumnControls = true,
  showRefresh,
  onRefresh,
  enableColumnResize = true,
  enableDragDrop = true,
  tableName = null // Table name for API persistence (e.g., 'filemanager', 'dataroommanagement')
}) => {
  console.log('🔵 EnhancedDataTableV2 RENDERED with tableName:', tableName);

  // Ensure data is always an array
  const safeData = Array.isArray(data) ? data : [];

  // State management
  const [columns, setColumns] = useState(initialColumns);
  const [hiddenColumns, setHiddenColumns] = useState(new Set());
  const [showColumnSettings, setShowColumnSettings] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [openMenus, setOpenMenus] = useState({});
  const [menuPositions, setMenuPositions] = useState({});
  const [sortConfig, setSortConfig] = useState({
    key: defaultSortKey,
    direction: defaultSortDirection
  });
  const [columnWidths, setColumnWidths] = useState({});
  const [tableKey, setTableKey] = useState(0);
  const [preferencesLoaded, setPreferencesLoaded] = useState(false);

  // Load table preferences from API on mount
  useEffect(() => {
    const loadPreferences = async () => {
      if (!tableName) {
        setPreferencesLoaded(true);
        return;
      }

      try {
        // Load column order
        const orderResponse = await tablePreferencesService.getColumnOrder(tableName);
        if (orderResponse?.data?.columnOrder && Array.isArray(orderResponse.data.columnOrder)) {
          const savedOrder = orderResponse.data.columnOrder;
          const reorderedColumns = savedOrder
            .map(key => initialColumns.find(col => col.key === key))
            .filter(col => col !== undefined);

          // Add any new columns that aren't in saved order
          const newColumns = initialColumns.filter(
            col => !savedOrder.includes(col.key)
          );

          if (reorderedColumns.length > 0) {
            setColumns([...reorderedColumns, ...newColumns]);
          }
        }

        // Load column visibility
        const visibilityResponse = await tablePreferencesService.getColumnVisibility(tableName);
        if (visibilityResponse?.data?.columnVisibility) {
          const savedVisibility = visibilityResponse.data.columnVisibility;
          const hidden = new Set();
          Object.entries(savedVisibility).forEach(([key, visible]) => {
            if (visible === false) {
              hidden.add(key);
            }
          });
          setHiddenColumns(hidden);
        }
      } catch (error) {
        console.error('Failed to load table preferences:', error);
      } finally {
        setPreferencesLoaded(true);
      }
    };

    loadPreferences();
  }, [tableName, JSON.stringify(initialColumns)]);

  // Update columns when initialColumns changes (only if preferences already loaded)
  useEffect(() => {
    if (preferencesLoaded && !tableName) {
      setColumns(initialColumns);
    }
  }, [JSON.stringify(initialColumns), preferencesLoaded, tableName]);

  // Handle responsive design
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Close menus when clicking outside or pressing Escape
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if click is outside of any action menu container
      if (!event.target.closest('.actions-menu-container')) {
        setOpenMenus({});
      }
    };

    const handleEscapeKey = (event) => {
      if (event.key === 'Escape') {
        setOpenMenus({});
      }
    };

    // Add event listeners
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('click', handleClickOutside);
    document.addEventListener('keydown', handleEscapeKey);
    
    // Cleanup
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('click', handleClickOutside);
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, []);

  // Save preferences to API
  const savePreferences = useCallback(async (newColumns, newHiddenColumns) => {
    if (!tableName) {
      console.log('No tableName provided, skipping save');
      return;
    }

    try {
      const columnOrder = newColumns.map(col => col.key);
      const columnVisibility = {};
      newColumns.forEach(col => {
        columnVisibility[col.key] = !newHiddenColumns.has(col.key);
      });

      console.log('Saving table preferences:', {
        tableName,
        columnOrder,
        columnVisibility
      });

      const response = await tablePreferencesService.updateTablePreferences(tableName, {
        columnOrder,
        columnVisibility
      });

      console.log('Table preferences saved successfully:', response);
    } catch (error) {
      console.error('Failed to save table preferences:', error);
    }
  }, [tableName]);

  // Column management functions
  const moveColumn = useCallback((fromIndex, toIndex) => {
    if (!enableDragDrop) return;
    console.log('moveColumn called:', { fromIndex, toIndex });
    const newColumns = [...columns];
    const [movedColumn] = newColumns.splice(fromIndex, 1);
    newColumns.splice(toIndex, 0, movedColumn);
    setColumns(newColumns);
    console.log('Calling savePreferences after moveColumn');
    savePreferences(newColumns, hiddenColumns);
  }, [columns, enableDragDrop, hiddenColumns, savePreferences]);

  const toggleHideColumn = useCallback((key) => {
    console.log('=== toggleHideColumn START ===');
    console.log('toggleHideColumn called for key:', key);
    console.log('Current hiddenColumns:', hiddenColumns);
    console.log('Current columns:', columns);
    console.log('tableName:', tableName);

    const newHiddenColumns = new Set(hiddenColumns);
    if (newHiddenColumns.has(key)) {
      console.log('Removing key from hidden columns');
      newHiddenColumns.delete(key);
    } else {
      // Prevent hiding all columns
      const visibleCount = columns.filter(c => !newHiddenColumns.has(c.key)).length;
      console.log('Visible count:', visibleCount);
      if (visibleCount > 1 || newHiddenColumns.has(key)) {
        console.log('Adding key to hidden columns');
        newHiddenColumns.add(key);
      }
    }
    console.log('New hiddenColumns:', newHiddenColumns);
    setHiddenColumns(newHiddenColumns);
    console.log('Calling savePreferences after toggleHideColumn');
    savePreferences(columns, newHiddenColumns);
    console.log('=== toggleHideColumn END ===');
  }, [hiddenColumns, columns, savePreferences, tableName]);

  const resetColumns = async () => {
    setColumns(initialColumns);
    setHiddenColumns(new Set());
    setColumnWidths({});
    setTableKey(prev => prev + 1); // Force re-render to clear inline styles

    // Reset preferences in API
    if (tableName) {
      try {
        const columnOrder = initialColumns.map(col => col.key);
        const columnVisibility = {};
        initialColumns.forEach(col => {
          columnVisibility[col.key] = true;
        });

        await tablePreferencesService.updateTablePreferences(tableName, {
          columnOrder,
          columnVisibility
        });
      } catch (error) {
        console.error('Failed to reset table preferences:', error);
      }
    }
  };

  // Column resize handler
  const handleColumnResize = useCallback((columnKey, width) => {
    setColumnWidths(prev => ({
      ...prev,
      [columnKey]: width
    }));
  }, []);

  // Sorting logic
  const handleSort = useCallback((key) => {
    let direction = 'asc';
    if (sortConfig?.key === key && sortConfig?.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
    
    // Call parent sort handler if provided
    if (onSort) {
      onSort(key, direction);
    }
  }, [sortConfig, onSort]);

  // Sort data locally if no external sort handler
  const sortedData = useMemo(() => {
    if (!sortConfig?.key || onSort) {
      return safeData;
    }

    return [...safeData].sort((a, b) => {
      const aValue = a[sortConfig.key];
      const bValue = b[sortConfig.key];

      if (aValue === null || aValue === undefined) return 1;
      if (bValue === null || bValue === undefined) return -1;

      if (aValue < bValue) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  }, [safeData, sortConfig, onSort]);

  // Mobile card view
  if (isMobile) {
    return (
      <div className="space-y-4">
        {loading ? (
          <ListLoader rows={5} />
        ) : sortedData.length === 0 ? (
          <div className="bg-white rounded-lg shadow p-8">
            <div className="text-center text-gray-500">
              {emptyMessage}
            </div>
          </div>
        ) : (
          sortedData.map((row, rowIndex) => (
            <div
              key={rowIndex}
              className="bg-white rounded-lg shadow p-4 space-y-3 border-l-4 border-primary-500"
              onClick={() => onRowClick && onRowClick(row)}
              style={{ cursor: onRowClick ? 'pointer' : 'default' }}
            >
              {columns
                .filter(col => !hiddenColumns.has(col.key))
                .map((column) => (
                  <div key={column.key} className="flex justify-between items-start">
                    <span className="text-sm font-medium text-gray-500">
                      {column.header || column.label}:
                    </span>
                    <span className="text-sm text-gray-900 text-right ml-2">
                      {column.render ? column.render(row[column.key], row) : row[column.key]}
                    </span>
                  </div>
                ))}
              {actions && (
                <div className="pt-2 border-t border-gray-100 flex justify-end">
                  {(() => {
                    const result = actions(row);
                    if (Array.isArray(result) && result.length > 0) {
                      const menuKey = `mobile_menu_${row.id || row.uuid || rowIndex}`;
                      return (
                        <div className="relative inline-block text-left actions-menu-container">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              // Calculate smart positioning
                              const rect = e.currentTarget.getBoundingClientRect();
                              const dropdownWidth = 192; // w-48 = 192px
                              const dropdownHeight = 200; // Approximate height for mobile menu

                              let newPosition = {
                                top: rect.bottom + 8,
                                left: rect.left
                              };

                              // Smart boundary adjustments
                              newPosition.left = Math.max(10, Math.min(newPosition.left, window.innerWidth - dropdownWidth - 10));
                              newPosition.top = Math.max(10, Math.min(newPosition.top, window.innerHeight - dropdownHeight - 10));

                              // Close all other menus and toggle current one
                              setOpenMenus(prev => {
                                const isCurrentlyOpen = prev[menuKey];
                                if (!isCurrentlyOpen) {
                                  setMenuPositions(prevPos => ({
                                    ...prevPos,
                                    [menuKey]: newPosition
                                  }));
                                }
                                return isCurrentlyOpen ? {} : { [menuKey]: true };
                              });
                            }}
                            className="p-2 rounded-lg transition-colors hover:bg-gray-100"
                            title="More actions"
                          >
                            <MoreVertical className="w-4 h-4" />
                          </button>
                          {openMenus[menuKey] && (
                            <div
                              className="fixed z-50 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5"
                              style={{
                                top: menuPositions[menuKey]?.top || 0,
                                left: menuPositions[menuKey]?.left || 0
                              }}
                            >
                              <div className="py-1" role="menu">
                                {result.map((action, actionIndex) => (
                                  <button
                                    key={actionIndex}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      action.onClick();
                                      setOpenMenus({});
                                    }}
                                    className={`flex items-center w-full px-3 py-1.5 text-sm text-left hover:bg-gray-100 ${action.className || 'text-gray-700'}`}
                                    role="menuitem"
                                  >
                                    <action.icon className="w-4 h-4 mr-2" />
                                    {action.label}
                                  </button>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    }
                    return result;
                  })()}
                </div>
              )}
            </div>
          ))
        )}
        
        {/* Mobile Pagination */}
        {pagination && pagination.last_page > 1 && (
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center justify-between">
              <div className="text-xs text-gray-600">
                Page {pagination.current_page} of {pagination.last_page}
              </div>
              <div className="flex items-center gap-1">
                {/* First page button */}
                <button
                  onClick={() => onPageChange(1)}
                  disabled={pagination.current_page === 1}
                  className="w-8 h-8 flex items-center justify-center text-gray-500 hover:text-gray-900 hover:bg-gray-100 rounded transition-colors disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-gray-500"
                  title="First page"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
                  </svg>
                </button>
                
                {/* Previous page button */}
                <button
                  onClick={() => onPageChange(pagination.current_page - 1)}
                  disabled={pagination.current_page === 1}
                  className="w-8 h-8 flex items-center justify-center text-gray-500 hover:text-gray-900 hover:bg-gray-100 rounded transition-colors disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-gray-500"
                  title="Previous page"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                
                {/* Next page button */}
                <button
                  onClick={() => onPageChange(pagination.current_page + 1)}
                  disabled={pagination.current_page === pagination.last_page}
                  className="w-8 h-8 flex items-center justify-center text-gray-500 hover:text-gray-900 hover:bg-gray-100 rounded transition-colors disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-gray-500"
                  title="Next page"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
                
                {/* Last page button */}
                <button
                  onClick={() => onPageChange(pagination.last_page)}
                  disabled={pagination.current_page === pagination.last_page}
                  className="w-8 h-8 flex items-center justify-center text-gray-500 hover:text-gray-900 hover:bg-gray-100 rounded transition-colors disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-gray-500"
                  title="Last page"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5l7 7-7 7M5 5l7 7-7 7" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Desktop table view
  return (
    <DndProvider backend={dndBackend}>
      <div className={`bg-white rounded-xl shadow-sm border border-gray-200 ${className}`}>
        {/* Table Controls */}
        {showColumnControls && (
          <div className="px-6 py-3 border-b border-gray-200 flex justify-between items-center bg-gray-50">
            <h3 className="text-sm font-medium text-gray-700">
              {sortedData.length} {sortedData.length === 1 ? 'item' : itemName}
            </h3>
            <div className="flex items-center gap-2">
              {showRefresh && onRefresh && (
                <button
                  onClick={onRefresh}
                  className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                  title="Refresh"
                >
                  <RefreshCw className="w-4 h-4" />
                </button>
              )}
              <button
                onClick={() => setShowColumnSettings(!showColumnSettings)}
                className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <Columns className="w-4 h-4" />
                Customize Columns
              </button>
              <button
                onClick={resetColumns}
                className="text-sm text-blue-600 hover:text-blue-700 font-medium px-2"
              >
                Reset
              </button>
            </div>
          </div>
        )}

        {/* Column Settings Panel */}
        {showColumnSettings && (
          <div className="px-6 py-4 bg-gray-100 border-b border-gray-200">
            <div className="mb-3">
              <p className="text-xs text-gray-600 mb-1">
                {enableDragDrop ? 'Drag columns to reorder. ' : ''}
                Click to toggle visibility.
                {enableColumnResize ? 'Resize columns by dragging borders.' : ''}
              </p>
              <p className="text-xs text-gray-500">
                Visible: {columns.filter(c => !hiddenColumns.has(c.key)).length} / {columns.length} columns
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              {columns.map((col) => (
                <button
                  key={col.key}
                  onClick={(e) => {
                    console.log('BUTTON CLICKED!', col.key);
                    e.stopPropagation();
                    toggleHideColumn(col.key);
                  }}
                  className={`inline-flex items-center gap-2 px-3 py-1.5 text-xs font-medium rounded-lg transition-all transform hover:scale-105 ${
                    hiddenColumns.has(col.key)
                      ? 'bg-gray-200 text-gray-500 hover:bg-gray-300'
                      : 'bg-primary-100 text-primary-700 hover:bg-primary-200 shadow-sm'
                  }`}
                >
                  {hiddenColumns.has(col.key) ? (
                    <EyeOff className="w-3 h-3" />
                  ) : (
                    <Eye className="w-3 h-3" />
                  )}
                  {col.header || col.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Table */}
        <div className="overflow-x-auto">
          {loading ? (
            <TableLoader rows={10} columns={columns.filter(c => !hiddenColumns.has(c.key)).length} />
          ) : sortedData.length === 0 ? (
            <div className="px-6 py-12 text-center text-gray-500">
              {emptyMessage}
            </div>
          ) : (
            <table key={tableKey} className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50 group">
                <tr>
                  {columns.map((column, index) =>
                    !hiddenColumns.has(column.key) ? (
                      <DraggableColumnHeader
                        key={column.key}
                        column={column}
                        index={index}
                        moveColumn={moveColumn}
                        hidden={hiddenColumns.has(column.key)}
                        onToggleHide={toggleHideColumn}
                        sortConfig={sortConfig}
                        onSort={handleSort}
                        width={columnWidths[column.key]}
                        onResize={enableColumnResize ? handleColumnResize : null}
                        showColumnSettings={showColumnSettings}
                      />
                    ) : null
                  )}
                  {actions && (
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  )}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {sortedData.map((row, rowIndex) => (
                  <tr
                    key={rowIndex}
                    onClick={() => onRowClick && onRowClick(row)}
                    className={`${onRowClick ? 'hover:bg-gray-50 cursor-pointer' : ''} transition-colors`}
                  >
                    {columns.map((column) =>
                      !hiddenColumns.has(column.key) ? (
                        <td 
                          key={column.key} 
                          className="px-4 py-3 text-sm text-gray-900"
                          style={{ width: columnWidths[column.key] || 'auto' }}
                        >
                          {column.render ? column.render(row[column.key], row) : row[column.key]}
                        </td>
                      ) : null
                    )}
                    {actions && (
                      <td className="px-4 py-3 text-sm text-gray-900 text-right">
                        {(() => {
                          const result = actions(row);
                          if (Array.isArray(result) && result.length > 0) {
                            // Always show dropdown menu for actions
                            const menuKey = `menu_${row.id || row.uuid || rowIndex}`;
                            return (
                              <div className="relative inline-block text-left actions-menu-container">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    // Calculate smart positioning
                                    const rect = e.currentTarget.getBoundingClientRect();
                                    const dropdownWidth = 192; // w-48 = 192px
                                    const dropdownHeight = 200; // Approximate height for desktop menu

                                    let newPosition = {
                                      top: rect.bottom + 8,
                                      left: rect.left
                                    };

                                    // Smart boundary adjustments
                                    newPosition.left = Math.max(10, Math.min(newPosition.left, window.innerWidth - dropdownWidth - 10));
                                    newPosition.top = Math.max(10, Math.min(newPosition.top, window.innerHeight - dropdownHeight - 10));

                                    // Close all other menus and toggle current one
                                    setOpenMenus(prev => {
                                      const isCurrentlyOpen = prev[menuKey];
                                      if (!isCurrentlyOpen) {
                                        setMenuPositions(prevPos => ({
                                          ...prevPos,
                                          [menuKey]: newPosition
                                        }));
                                      }
                                      // If current menu is open, close it. Otherwise open only this one
                                      return isCurrentlyOpen ? {} : { [menuKey]: true };
                                    });
                                  }}
                                  className="p-1.5 rounded-lg transition-colors hover:bg-gray-100"
                                  title="More actions"
                                >
                                  <MoreVertical className="w-4 h-4" />
                                </button>
                                {openMenus[menuKey] && (
                                  <div
                                    className="fixed z-50 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5"
                                    style={{
                                      top: menuPositions[menuKey]?.top || 0,
                                      left: menuPositions[menuKey]?.left || 0
                                    }}
                                  >
                                    <div className="py-1" role="menu">
                                      {result.map((action, actionIndex) => (
                                        <button
                                          key={actionIndex}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            action.onClick();
                                            setOpenMenus({});
                                          }}
                                          className={`flex items-center w-full px-3 py-1.5 text-sm text-left hover:bg-gray-100 ${action.className || 'text-gray-700'}`}
                                          role="menuitem"
                                        >
                                          <action.icon className="w-4 h-4 mr-2" />
                                          {action.label}
                                        </button>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </div>
                            );
                          }
                          return result;
                        })()}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Pagination */}
        {pagination && pagination.last_page > 1 && (
          <div className="px-6 py-4 bg-white border-t border-gray-200 flex items-center justify-between">
            <div className="text-sm text-gray-600">
              Showing {((pagination.current_page - 1) * pagination.per_page) + 1} to{' '}
              {Math.min(pagination.current_page * pagination.per_page, pagination.total)} of{' '}
              {pagination.total} {itemName}
            </div>
            <div className="flex items-center gap-1">
              {/* First page button */}
              <button
                onClick={() => onPageChange(1)}
                disabled={pagination.current_page === 1}
                className="w-8 h-8 flex items-center justify-center text-gray-500 hover:text-gray-900 hover:bg-gray-100 rounded transition-colors disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-gray-500"
                title="First page"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
                </svg>
              </button>
              
              {/* Previous page button */}
              <button
                onClick={() => onPageChange(pagination.current_page - 1)}
                disabled={pagination.current_page === 1}
                className="w-8 h-8 flex items-center justify-center text-gray-500 hover:text-gray-900 hover:bg-gray-100 rounded transition-colors disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-gray-500"
                title="Previous page"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              
              {/* Page numbers */}
              {getPageNumbers(pagination).map((pageNum, index) => (
                pageNum === '...' ? (
                  <span key={`ellipsis-${index}`} className="w-8 h-8 flex items-center justify-center text-gray-400">...</span>
                ) : (
                  <button
                    key={pageNum}
                    onClick={() => onPageChange(pageNum)}
                    className={`w-8 h-8 flex items-center justify-center rounded text-sm font-medium transition-all ${
                      pageNum === pagination.current_page
                        ? 'text-white'
                        : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                    }`}
                    style={pageNum === pagination.current_page ? { backgroundColor: '#3872FA' } : {}}
                  >
                    {pageNum}
                  </button>
                )
              ))}
              
              {/* Next page button */}
              <button
                onClick={() => onPageChange(pagination.current_page + 1)}
                disabled={pagination.current_page === pagination.last_page}
                className="w-8 h-8 flex items-center justify-center text-gray-500 hover:text-gray-900 hover:bg-gray-100 rounded transition-colors disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-gray-500"
                title="Next page"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
              
              {/* Last page button */}
              <button
                onClick={() => onPageChange(pagination.last_page)}
                disabled={pagination.current_page === pagination.last_page}
                className="w-8 h-8 flex items-center justify-center text-gray-500 hover:text-gray-900 hover:bg-gray-100 rounded transition-colors disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-gray-500"
                title="Last page"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5l7 7-7 7M5 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          </div>
        )}
      </div>
    </DndProvider>
  );
};

// Helper function to get page numbers with ellipsis
const getPageNumbers = (pagination) => {
  const { current_page, last_page } = pagination;
  const delta = 2;
  const range = [];
  const rangeWithDots = [];
  let l;

  for (let i = 1; i <= last_page; i++) {
    if (i === 1 || i === last_page || (i >= current_page - delta && i <= current_page + delta)) {
      range.push(i);
    }
  }

  range.forEach((i) => {
    if (l) {
      if (i - l === 2) {
        rangeWithDots.push(l + 1);
      } else if (i - l !== 1) {
        rangeWithDots.push('...');
      }
    }
    rangeWithDots.push(i);
    l = i;
  });

  return rangeWithDots;
};

export default EnhancedDataTableV2;